import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShares } from '../hooks/useShares';

// "Shared files" summary panel — lists every active link the user has created.
export default function SharedFilesModal({ open, onClose }) {
  const { t } = useTranslation('share');
  const { shares, loading, loadShares, getShareLink, revokeShare } = useShares();
  const [copiedId, setCopiedId] = useState(null);
  const [busyId, setBusyId]     = useState(null);

  function timeLeft(expiresAt) {
    if (!expiresAt) return null;
    const ms = new Date(expiresAt) - new Date();
    if (ms <= 0) return t('sharedfiles_expired');
    const days = Math.ceil(ms / 86400000);
    return t('sharedfiles_days_left', { count: days });
  }

  useEffect(() => { if (open) loadShares(); }, [open]); // eslint-disable-line

  async function handleCopy(share) {
    setBusyId(share.id);
    try {
      const link = await getShareLink(share);
      await navigator.clipboard.writeText(link);
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 1500);
    } finally { setBusyId(null); }
  }

  async function handleRevoke(share) {
    if (!window.confirm(t('sharedfiles_revoke_confirm', { name: share.name }))) return;
    setBusyId(share.id);
    try { await revokeShare(share.id); } finally { setBusyId(null); }
  }

  if (!open) return null;

  return (
    <div className="vz-modal-overlay" onClick={onClose}>
      <div className="vz-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <h3 className="vz-modal-title">{t('sharedfiles_title')}</h3>

        {loading && <p style={{ fontSize: 13, color: 'var(--text3)' }}>{t('sharedfiles_loading')}</p>}
        {!loading && shares.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--text3)' }}>{t('sharedfiles_empty')}</p>
        )}

        {!loading && shares.length > 0 && (
          <div className="move-folder-list" style={{ maxHeight: 400 }}>
            {shares.map(s => (
              <div key={s.id} className="share-list-item">
                <span style={{ fontSize: 18 }}>{s.type === 'folder' ? '📁' : '📄'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text)', fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text3)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {s.hasPassword && <span>{t('sharedfiles_password_badge')}</span>}
                    <span>{t('sharedfiles_item_count', { count: s.itemCount })}</span>
                    <span>⬇ {s.downloadCount}{s.maxDownloads ? `/${s.maxDownloads}` : ''}</span>
                    {s.expiresAt && <span className={s.expired ? 'share-expired' : ''}>{timeLeft(s.expiresAt)}</span>}
                    {s.expired && !s.expiresAt && <span className="share-expired">{t('sharedfiles_limit_reached')}</span>}
                  </div>
                </div>
                <button className="vz-btn vz-btn-sm vz-btn-blue" disabled={busyId === s.id || s.expired} onClick={() => handleCopy(s)} title={t('sharedfiles_copy_title')} aria-label={t('sharedfiles_copy_title')}>
                  {copiedId === s.id ? '✓' : '📋'}
                </button>
                <button className="vz-btn vz-btn-sm vz-btn-red" disabled={busyId === s.id} onClick={() => handleRevoke(s)} title={t('sharedfiles_revoke_title')} aria-label={t('sharedfiles_revoke_title')}>🗑</button>
              </div>
            ))}
          </div>
        )}

        <div className="vz-modal-btns">
          <button className="vz-btn vz-btn-ghost" onClick={onClose}>{t('sharedfiles_close_btn')}</button>
        </div>
      </div>
    </div>
  );
}
